import { periodGrowthPercent } from './forecastProjection';

export const NEXT_QUARTER_MONTH_COUNT = 3;

const PARTIAL_SECTION_SCORE = 0.5;
const REVIEW_READY_SCORE = 0.85;
const FORECAST_SECTION_COUNT = 6;
const SUBMITTED_STATUSES = new Set(['submitted', 'in_review', 'approved', 'loaded']);
export const FORECAST_IMPACT_EPS = 0.05; // $50k in $MM display units.

export const FORECAST_IMPACT_LINE_DEFS = [
  { field: 'endingLoans', label: 'Loans (EOP)', measure: 'EOP' },
  { field: 'endingDeposits', label: 'Deposits (EOP)', measure: 'EOP' },
  { field: 'interestIncome', label: 'Interest income', measure: 'flow' },
  { field: 'interestExpense', label: 'Interest expense', measure: 'flow' },
  { field: 'nii', label: 'Net interest income', measure: 'flow' },
  { field: 'nonInterestIncome', label: 'Non-interest income', measure: 'flow' },
  { field: 'nonInterestExpense', label: 'Non-interest expense', measure: 'flow' },
  { field: 'creditProvision', label: 'Provision', measure: 'flow' },
  { field: 'preTaxIncome', label: 'Pre-tax income', measure: 'flow' },
  { field: 'taxExpense', label: 'Tax expense', measure: 'flow' },
  { field: 'netIncome', label: 'Net income', measure: 'flow' }
];

export const FORECAST_LEVER_PRIMARY_FIELDS = {
  loanGrowthPts: 'endingLoans',
  depositGrowthPts: 'endingDeposits',
  feeAdjMM: 'nonInterestIncome',
  expenseAdjMM: 'nonInterestExpense',
  compAdjMM: 'nonInterestExpense',
  provisionAdjMM: 'creditProvision'
};

const AI_PROPOSED_ACCOUNTS_BY_LEVER = {
  loanGrowthPts: ['139'],
  depositGrowthPts: ['219', '229', '239'],
  feeAdjMM: ['529'],
  expenseAdjMM: ['609', '619', '659', '709', '759', '779', '789'],
  compAdjMM: ['609', '619'],
  provisionAdjMM: ['850']
};

const CASCADE_DERIVED_ACCOUNTS_BY_LEVER = {
  loanGrowthPts: ['419', '850', '909'],
  depositGrowthPts: ['459', '4290', '909'],
  feeAdjMM: ['909'],
  expenseAdjMM: ['909'],
  compAdjMM: ['909'],
  provisionAdjMM: ['909']
};

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const forecastImpactCellDelta = (line, year) => {
  const cell = line?.byYear?.[year] || line?.byYear?.[String(year)] || {};
  return (Number(cell.new) || 0) - (Number(cell.old) || 0);
};

export const formatForecastImpactDelta = (value) => {
  const v = Number(value) || 0;
  if (Math.abs(v) < FORECAST_IMPACT_EPS) return '—';
  return `${v > 0 ? '+' : '-'}$${Math.abs(v).toFixed(1)}`;
};

export const buildForecastImpactDiff = ({
  lines,
  simulate,
  leverMap,
  years = [],
  lineDefs = FORECAST_IMPACT_LINE_DEFS,
  eps = FORECAST_IMPACT_EPS
} = {}) => {
  const simulatedLines = Array.isArray(lines)
    ? lines
    : typeof simulate === 'function'
      ? simulate(leverMap || {}) || []
      : [];
  const byField = Object.fromEntries(simulatedLines.map((line) => [line.field, line]));

  return lineDefs
    .map((def) => {
      const line = byField[def.field];
      if (!line) return null;
      const totalDelta = years.reduce((sum, year) => sum + forecastImpactCellDelta(line, year), 0);
      return { ...def, ...line, measure: line.measure || def.measure, label: line.label || def.label, totalDelta };
    })
    .filter(Boolean)
    .filter((line) => years.some((year) => Math.abs(forecastImpactCellDelta(line, year)) >= eps));
};

export const buildAiForecastProvenanceByAccount = (leverIds = [], existing = {}) => {
  const next = { ...(existing || {}) };
  const ids = Array.isArray(leverIds) ? leverIds : [leverIds];

  ids.forEach((leverId) => {
    (AI_PROPOSED_ACCOUNTS_BY_LEVER[leverId] || []).forEach((account) => {
      next[account] = 'ai-proposed';
    });
    (CASCADE_DERIVED_ACCOUNTS_BY_LEVER[leverId] || []).forEach((account) => {
      if (next[account] !== 'ai-proposed') next[account] = 'cascade-derived';
    });
  });

  return next;
};

export const resolveForecastProvenance = (account, provenanceByAccount = {}) =>
  provenanceByAccount?.[account] || 'baseline-carry';

export const relativeVariancePercent = (value, baseline) => {
  const current = finiteNumber(value);
  const comparison = finiteNumber(baseline);
  if (current === null || comparison === null) return null;
  if (comparison === 0) return current === 0 ? 0 : null;
  return ((current - comparison) / Math.abs(comparison)) * 100;
};

const pointVariance = (current, baseline) =>
  Number.isFinite(current) && Number.isFinite(baseline) ? current - baseline : null;

export const calculateInputProgress = ({
  submissionStatus,
  baselineDecision,
  contextAcknowledged,
  leverAttentionCount = 0,
  totalOverrideCount = 0,
  workforceChanged = false,
  workforceReviewed = false,
  resultsValid = false,
  reviewNarrativesComplete = false,
  reviewIssueCount = 0
}) => {
  if (SUBMITTED_STATUSES.has(submissionStatus)) {
    return { percent: 100, detail: `${FORECAST_SECTION_COUNT} of ${FORECAST_SECTION_COUNT} sections complete` };
  }

  const leversComplete = totalOverrideCount > 0 || leverAttentionCount === 0;
  const reviewReady = reviewNarrativesComplete && reviewIssueCount === 0;
  const sectionScores = [
    baselineDecision === 'confirmed' ? 1 : baselineDecision === 'flagged' ? PARTIAL_SECTION_SCORE : 0,
    contextAcknowledged ? 1 : 0,
    leversComplete ? 1 : 0,
    workforceChanged || workforceReviewed ? 1 : 0,
    resultsValid ? 1 : 0,
    // Review is deliberately short of complete until the package enters a submitted state.
    reviewReady ? REVIEW_READY_SCORE : 0
  ];
  const percent = Math.round(
    (sectionScores.reduce((sum, score) => sum + score, 0) / FORECAST_SECTION_COUNT) * 100
  );
  const completeCount = sectionScores.filter((score) => score === 1).length;
  return { percent, detail: `${completeCount} of ${FORECAST_SECTION_COUNT} sections complete` };
};

export const calculateLiveImpactSummary = ({
  annualResults = [],
  baselineAnnualSeries = [],
  adjustedMonthlySeries = [],
  baselineMonthlySeries = [],
  basePortfolio = {},
  comparisonHydrated = true
}) => {
  const fy1 = annualResults[0];
  const baselineFy1 = baselineAnnualSeries[0];
  // "Next Qtr" means the first three modeled months, even when the model starts after a data lag.
  const adjustedQuarter = adjustedMonthlySeries.slice(0, NEXT_QUARTER_MONTH_COUNT);
  const baselineQuarter = baselineMonthlySeries.slice(0, NEXT_QUARTER_MONTH_COUNT);
  const adjustedQuarterEnd = adjustedQuarter[adjustedQuarter.length - 1];
  const baselineQuarterEnd = baselineQuarter[baselineQuarter.length - 1];
  const adjustedQuarterNetIncome = adjustedQuarter.reduce((sum, row) => sum + Number(row.netIncome || 0), 0);
  const baselineQuarterNetIncome = baselineQuarter.reduce((sum, row) => sum + Number(row.netIncome || 0), 0);
  const adjustedQuarterLoanGrowth = periodGrowthPercent(basePortfolio.loans, adjustedQuarterEnd?.endingLoans);
  const baselineQuarterLoanGrowth = periodGrowthPercent(basePortfolio.loans, baselineQuarterEnd?.endingLoans);
  const adjustedQuarterDepositGrowth = periodGrowthPercent(
    basePortfolio.deposits,
    adjustedQuarterEnd?.endingDeposits
  );
  const baselineQuarterDepositGrowth = periodGrowthPercent(
    basePortfolio.deposits,
    baselineQuarterEnd?.endingDeposits
  );

  return {
    comparisonHydrated,
    fy1NetIncome: finiteNumber(fy1?.adjusted),
    fy1NetIncomeVariance: comparisonHydrated ? finiteNumber(fy1?.variancePct) : null,
    quarterNetIncome: adjustedQuarter.length ? adjustedQuarterNetIncome : null,
    quarterNetIncomeVariance: comparisonHydrated && adjustedQuarter.length
      ? relativeVariancePercent(adjustedQuarterNetIncome, baselineQuarterNetIncome)
      : null,
    fy1LoanGrowth: finiteNumber(fy1?.loanGrowth),
    fy1LoanGrowthVariance: comparisonHydrated ? pointVariance(
      finiteNumber(fy1?.loanGrowth),
      finiteNumber(baselineFy1?.loanGrowth)
    ) : null,
    quarterLoanGrowth: adjustedQuarter.length ? adjustedQuarterLoanGrowth : null,
    quarterLoanGrowthVariance: comparisonHydrated
      ? pointVariance(adjustedQuarterLoanGrowth, baselineQuarterLoanGrowth)
      : null,
    fy1DepositGrowth: finiteNumber(fy1?.depositGrowth),
    fy1DepositGrowthVariance: comparisonHydrated ? pointVariance(
      finiteNumber(fy1?.depositGrowth),
      finiteNumber(baselineFy1?.depositGrowth)
    ) : null,
    quarterDepositGrowth: adjustedQuarter.length ? adjustedQuarterDepositGrowth : null,
    quarterDepositGrowthVariance: comparisonHydrated
      ? pointVariance(adjustedQuarterDepositGrowth, baselineQuarterDepositGrowth)
      : null
  };
};

export const calculateHydratedLiveImpactSummary = ({
  hydrated,
  baselineHydrated,
  ...impactInputs
}) => {
  if (!hydrated) {
    return calculateLiveImpactSummary({ comparisonHydrated: false });
  }

  return calculateLiveImpactSummary({
    ...impactInputs,
    comparisonHydrated: Boolean(baselineHydrated)
  });
};

// ------------------------------------------------------------------ M4 anchors
// Driver-view anchor: the top-down "pure driver view" of a unit under the
// published Base scenario. The workspace compares the working plan against the
// anchor and requires a justification narrative when a line deviates beyond
// threshold. These helpers are pure so the server (forecast_macro /
// forecast_quality_gates) and the client agree on the deviation math and the
// narrative-trigger vocabulary. Mirrors ANCHOR_METRIC_FIELDS /
// compute_driver_view_deviations in forecast_macro.py.

export const DRIVER_VIEW_METRIC_FIELDS = {
  netIncome: 'netIncome',
  nii: 'nii',
  fees: 'nonInterestIncome',
  expense: 'nonInterestExpense',
  provision: 'creditProvision',
  loans: 'endingLoans',
  deposits: 'endingDeposits'
};

export const DRIVER_VIEW_METRIC_LABELS = {
  netIncome: 'Net income',
  nii: 'NII',
  fees: 'Fees',
  expense: 'Expense',
  provision: 'Provision',
  loans: 'Loans',
  deposits: 'Deposits'
};

// Metrics that trigger a required narrative when they breach threshold.
export const DRIVER_VIEW_GATE_METRICS = ['netIncome', 'loans', 'deposits'];
export const DRIVER_VIEW_NI_THRESHOLD_PCT = 3;
export const DRIVER_VIEW_BALANCE_THRESHOLD_PCT = 2;

const fiscalYearNumber = (row) => {
  if (row?.fiscalYearNumber != null) return Number(row.fiscalYearNumber);
  const raw = String(row?.year ?? '').replace(/^FY/i, '');
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Reduce driver-model annual rows to the anchor snapshot's byYear shape. */
export const buildDriverViewPlanSnapshot = (annualSeries = []) => {
  const byYear = {};
  (annualSeries || []).forEach((row) => {
    const year = fiscalYearNumber(row);
    if (year == null) return;
    const metrics = {};
    Object.entries(DRIVER_VIEW_METRIC_FIELDS).forEach(([metric, field]) => {
      const value = Number(row?.[field]);
      metrics[metric] = Number.isFinite(value) ? value : 0;
    });
    byYear[String(year)] = metrics;
  });
  return byYear;
};

const thresholdForMetric = (metric, niThreshold, balanceThreshold) =>
  metric === 'netIncome' ? niThreshold : balanceThreshold;

/**
 * Compare the plan snapshot against the anchor snapshot and return per-metric,
 * per-year deviations flagged against their thresholds. `anchorSnapshot` is the
 * server anchor payload ({ byYear: { year: { metric: value } } }).
 */
export const buildDriverViewDeltas = ({
  anchorSnapshot,
  planByYear,
  metrics = DRIVER_VIEW_GATE_METRICS,
  niThreshold = DRIVER_VIEW_NI_THRESHOLD_PCT,
  balanceThreshold = DRIVER_VIEW_BALANCE_THRESHOLD_PCT
} = {}) => {
  const anchorByYear = (anchorSnapshot && anchorSnapshot.byYear) || {};
  const plan = planByYear || {};
  const years = Array.from(
    new Set([...Object.keys(anchorByYear), ...Object.keys(plan)].map((year) => String(year)))
  ).sort((a, b) => Number(a) - Number(b));
  const deltas = [];
  years.forEach((year) => {
    const anchorRow = anchorByYear[year] || anchorByYear[Number(year)] || {};
    const planRow = plan[year] || plan[Number(year)] || {};
    metrics.forEach((metric) => {
      const anchorValue = Number(anchorRow[metric]) || 0;
      const planValue = Number(planRow[metric]) || 0;
      const threshold = thresholdForMetric(metric, niThreshold, balanceThreshold);
      let deviationPct = null;
      if (anchorValue === 0) {
        deviationPct = planValue === 0 ? 0 : null;
      } else {
        deviationPct = ((planValue - anchorValue) / Math.abs(anchorValue)) * 100;
      }
      const exceeded = deviationPct != null && Math.abs(deviationPct) > threshold;
      deltas.push({
        metric,
        metricLabel: DRIVER_VIEW_METRIC_LABELS[metric] || metric,
        year: Number(year),
        anchor: anchorValue,
        plan: planValue,
        delta: planValue - anchorValue,
        deviationPct,
        threshold,
        exceeded,
        narrativeTrigger: `driver-view-${metric}-${year}`
      });
    });
  });
  return deltas;
};

// ---- M5 propagation: macro-scenario staleness (pure, unit-tested) ----

/** Fiscal years used when diffing macro-variable paths. */
export const MACRO_DIFF_YEARS = [2027, 2028, 2029, 2030, 2031];

const normalizeScenarioKey = (key) => String(key || '').trim().toLowerCase();

/**
 * Pick the latest published version of a scenario key from a scenario list.
 * The list is the shape returned by apiService.fetchMacroScenarios (array or
 * { scenarios: [...] }). Returns the scenario object or null.
 */
export const latestPublishedScenario = (scenarios, scenarioKey = 'Base') => {
  const rows = Array.isArray(scenarios)
    ? scenarios
    : (scenarios && Array.isArray(scenarios.scenarios) ? scenarios.scenarios : []);
  const target = normalizeScenarioKey(scenarioKey);
  return rows
    .filter((row) => row
      && row.status === 'published'
      && normalizeScenarioKey(row.key || row.scenario_key) === target)
    .reduce((best, row) => {
      const version = Number(row.version || 0);
      if (!best || version > Number(best.version || 0)) return row;
      return best;
    }, null);
};

/**
 * Pure stale-detection: compare a loaded submission's saved macro pointer
 * (scenarioKey + version) against the latest published version of that key.
 * Never throws; returns a plain descriptor so the workspace can render a chip
 * and callers can unit test without rendering the monolith.
 *
 * savedVersion/publishedVersion are coerced to numbers; a missing published
 * version (nothing published yet) is never "stale".
 */
export const detectMacroScenarioStaleness = ({
  savedScenarioKey = 'Base',
  savedScenarioVersion,
  scenarios
} = {}) => {
  const scenarioKey = savedScenarioKey || 'Base';
  const published = latestPublishedScenario(scenarios, scenarioKey);
  const publishedVersion = published ? Number(published.version) : null;
  const savedVersion = savedScenarioVersion == null ? null : Number(savedScenarioVersion);
  const hasPublished = Number.isFinite(publishedVersion);
  const hasSaved = Number.isFinite(savedVersion);
  // Stale only when we know both and the published version is strictly newer.
  const stale = hasPublished && hasSaved && publishedVersion > savedVersion;
  return {
    scenarioKey,
    savedVersion: hasSaved ? savedVersion : null,
    publishedVersion: hasPublished ? publishedVersion : null,
    stale,
    published: published || null,
    label: stale
      ? `Macro v${publishedVersion} published — your plan uses v${savedVersion}`
      : ''
  };
};

const macroYearValue = (variables, key, year, fallback = 0) => {
  const raw = variables ? variables[key] : undefined;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const value = raw[String(year)] ?? raw[year];
    return Number.isFinite(Number(value)) ? Number(value) : fallback;
  }
  return Number.isFinite(Number(raw)) ? Number(raw) : fallback;
};

/**
 * Per-variable, per-year old -> new diff between two macro-scenario variable
 * maps (same presentation approach as the console's MX_03 variable diff). Only
 * changed cells are returned. `variableLabels` optionally maps keys to labels.
 */
export const buildMacroVariableDiff = ({
  savedVariables = {},
  publishedVariables = {},
  years = MACRO_DIFF_YEARS,
  variableLabels = {},
  epsilon = 1e-7
} = {}) => {
  const keys = Array.from(new Set([
    ...Object.keys(savedVariables || {}),
    ...Object.keys(publishedVariables || {})
  ])).sort();
  const rows = [];
  keys.forEach((key) => {
    years.forEach((year) => {
      const from = macroYearValue(savedVariables, key, year);
      const to = macroYearValue(publishedVariables, key, year);
      const delta = to - from;
      if (Math.abs(delta) <= epsilon) return;
      rows.push({
        key: `${key}-${year}`,
        variable: variableLabels[key] || key,
        variableKey: key,
        year: Number(year),
        from,
        to,
        delta
      });
    });
  });
  return rows;
};

/** Narrative triggers (id + label) required by breaching driver-view deltas. */
export const buildDriverViewNarrativeTriggers = (deltas = []) =>
  (deltas || [])
    .filter((delta) => delta && delta.exceeded)
    .map((delta) => ({
      id: delta.narrativeTrigger,
      label: `${delta.metricLabel} ${delta.year} is ${delta.deviationPct >= 0 ? '+' : ''}${Number(delta.deviationPct).toFixed(1)}% vs the driver view (threshold ±${delta.threshold}%) — explain the plan's departure from the top-down anchor`
    }));
